import { graphql } from 'gatsby';
import styled from '@emotion/styled';
import React from 'react';

import Default from '~/templates/Default';

import Seo from '~/layout/SEO';
import Row from '~/layout/Row';
import Column from '~/layout/Column';

import Breadcrumbs from '~/components/Breadcrumbs';
import BlockList from '~/components/BlockList';
import CtaBar from '~/components/blocks/acf/CtaBar';
import ArticleCards from '~/components/blocks/acf/ArticleCards';

import FeaturedStory from '~/components/blocks/acf/FeaturedStory';

import { setMediumSpacing } from '~/utilities/spacing';

const ContentWrapper = styled.div`
    ${setMediumSpacing('margin-bottom')};
`;

const Article = ({ data }) => {
    const {
        wp: {
            siteSettings: {
                blogCtas: { blogCtas: globalCtas }
            }
        },
        wpPost: {
            title,
            excerpt,
            featuredImage,
            categories,
            blocks,
            blogCtas: { blogCtas }
        },
        allWpPost: { nodes: cards }
    } = data;

    const ctas = blogCtas?.length
        ? blogCtas
        : globalCtas?.length
          ? globalCtas
          : [];

    // TODO: ADD PATHS TO OPTIONS PAGE
    const crumbs = [
        {
            pathname: '/newsroom/',
            crumbLabel: 'Newsroom'
        }
    ];

    let category = {};

    if (categories?.nodes?.length) {
        category = {
            url: `/category/${categories.nodes[0].slug}`,
            title: `More from ${categories.nodes[0].name}`
        };
        crumbs.push({
            pathname: category.url,
            crumbLabel: categories.nodes[0].name
        });
    }

    if (title) {
        crumbs.push({
            crumbLabel: title
        });
    }

    return (
        <Default>
            <FeaturedStory
                {...{ title, excerpt, featuredImage }}
                globalBlockOptions={{ marginBottom: 'none' }}
            />
            <Breadcrumbs {...{ crumbs }} icon="newspaper" />
            <Row>
                <Column spanTablet={10} startTablet={2} span={12} start={1}>
                    <ContentWrapper>
                        <BlockList {...{ blocks }} />
                    </ContentWrapper>
                </Column>
            </Row>

            {cards?.length ? (
                <ArticleCards
                    {...{ cards }}
                    link={category}
                    numberOfColumns={3}
                    backgroundColor="lightGray"
                    globalBlockOptions={{
                        marginBottom: 'none'
                    }}
                />
            ) : null}

            {ctas.length ? (
                <CtaBar
                    variant="threeColumn"
                    backgroundColor="blue"
                    globalBlockOptions={{
                        marginBottom: 'none'
                    }}
                    {...{ ctas }}
                />
            ) : null}
        </Default>
    );
};

export default Article;

export const Head = ({
    location,
    data: {
        wpPost: { seo: seoFragment }
    }
}) => <Seo {...{ location, seoFragment }} />;

export const query = graphql`
    query Article($id: String!, $termId: String) {
        wp {
            siteSettings {
                blogCtas {
                    blogCtas {
                        copy
                        link {
                            url
                            title
                        }
                        useComparisonTable
                        comparisonTableProductCategory {
                            id
                        }
                    }
                }
            }
        }
        wpPost(id: { eq: $id }) {
            id
            title
            excerpt
            featuredImage {
                node {
                    id
                    sourceUrl
                    altText
                    localFile {
                        childImageSharp {
                            gatsbyImageData(aspectRatio: 1.4)
                        }
                    }
                }
            }
            categories {
                nodes {
                    link
                    name
                    slug
                    id
                }
            }
            blogCtas {
                blogCtas {
                    copy
                    link {
                        url
                        title
                    }
                    useComparisonTable
                    comparisonTableProductCategory {
                        id
                    }
                }
            }
            seo {
                ...SeoFragment
            }
            blocks {
                ...BlockListFragment
            }
        }
        allWpPost(
            limit: 3
            filter: {
                id: { ne: $id }
                categories: { nodes: { elemMatch: { id: { eq: $termId } } } }
            }
            sort: { date: DESC }
        ) {
            nodes {
                title
                uri
                featuredImage {
                    node {
                        id
                        altText
                        sourceUrl
                        localFile {
                            childImageSharp {
                                gatsbyImageData(aspectRatio: 1.4)
                            }
                        }
                    }
                }
            }
        }
    }
`;
